import React, { useContext, createContext } from 'react';
import { Select as ReactSelect, Form, Row, Col, ToggleButtonGroup, ToggleButton } from '../../../ui-kit';
import GeneralAvailabilityCodeSelector from '../../../general/AvailabilityCodeSelector';
import GeneralAvailabilityRequirementsSelector from '../../../general/AvailabilityRequirementsSelector';
import GeneralGroupSelector from '../../../general/GroupSelector';

const calculateOnChangeHandler = (fieldName, onChange, updateWidgetSettingsField) => {
  if (onChange) return onChange;

  return event => {
    const { target } = event;
    let value;
    if (target === undefined) {
      // happens in case of the toggle button. The event is actually the value
      // itself
      value = event;
    } else {
      value = target.type === 'checkbox' ? target.checked : target.value;
    }
    updateWidgetSettingsField(fieldName, value);
  };
};

// helper method for the Select component. A simple array [1, 2, 3], is
// converted into [{value: 1, name: 1}, {value: 2, name: 2}, {value: 3, name:
// 3}]. If it already has this format, it is unchanged
const convertOptions = options => {
  if (options.length === 0) return options;
  if (typeof options[0] === 'object') return options;
  return options.map(o => ({ value: String(o), name: o }));
};

const Field = ({ label, description, children, labelColWidth = 9, forControl }) => (
  <Row className="mb-3">
    {labelColWidth !== 0 && (
      <Col sm={labelColWidth}>
        <Row>
          <Form.Label className="mb-0" htmlFor={forControl}>
            {label}
          </Form.Label>
        </Row>
        {description && (
          <Row>
            <Form.Text className="mt-0" muted>
              {description}
            </Form.Text>
          </Row>
        )}
      </Col>
    )}
    <Col sm={12 - labelColWidth}>{children}</Col>
  </Row>
);

const ToggleField = ({
  fieldName,
  label,
  description,
  value,
  onChange,
  disabled = false,
  updateWidgetSettingsField
}) => {
  const context = useContext(SettingsFormContext) || {};
  onChange = onChange || context.onChange;
  updateWidgetSettingsField = onChange || context.updateWidgetSettingsField;
  value = value === undefined ? context.widgetSettings[fieldName] : value;

  return (
    <Field label={label} description={description} forControl={fieldName}>
      <Form.Check
        className="float-end"
        type="switch"
        checked={value || false}
        disabled={disabled}
        id={fieldName}
        onChange={calculateOnChangeHandler(fieldName, onChange, updateWidgetSettingsField)}
      />
    </Field>
  );
};

const InputField = ({
  fieldName,
  label,
  description,
  value,
  onChange,
  updateWidgetSettingsField,
  type = 'text',
  labelColWidth = 9
}) => {
  const context = useContext(SettingsFormContext) || {};
  onChange = onChange || context.onChange;
  updateWidgetSettingsField = onChange || context.updateWidgetSettingsField;
  value = value || context.widgetSettings[fieldName];

  return (
    <Field label={label} description={description} labelColWidth={labelColWidth}>
      <Form.Control
        value={value}
        type={type}
        onChange={calculateOnChangeHandler(fieldName, onChange, updateWidgetSettingsField)}
      />
    </Field>
  );
};

const Select = ({
  fieldName,
  label,
  description,
  labelColWidth = 9,
  value,
  onChange,
  updateWidgetSettingsField,
  options
}) => {
  const context = useContext(SettingsFormContext) || {};
  onChange = onChange || context.onChange;
  updateWidgetSettingsField = onChange || context.updateWidgetSettingsField;
  value = value || context.widgetSettings[fieldName];

  return (
    <Field label={label} description={description} labelColWidth={labelColWidth}>
      <Form.Select value={value} onChange={calculateOnChangeHandler(fieldName, onChange, updateWidgetSettingsField)}>
        {convertOptions(options).map(o => (
          <option key={o.value} value={o.value}>
            {o.name}
          </option>
        ))}
      </Form.Select>
    </Field>
  );
};

const RadioButtonGroup = ({
  fieldName,
  labelColWidth = 9,
  label,
  description,
  value,
  onChange,
  updateWidgetSettingsField,
  options
}) => {
  const context = useContext(SettingsFormContext) || {};
  onChange = onChange || context.onChange;
  updateWidgetSettingsField = onChange || context.updateWidgetSettingsField;
  value = value || context.widgetSettings[fieldName];

  return (
    <Field label={label} description={description} labelColWidth={labelColWidth}>
      <ToggleButtonGroup
        className="d-flex justify-content-end"
        name={fieldName}
        type="radio"
        value={value}
        onChange={calculateOnChangeHandler(fieldName, onChange, updateWidgetSettingsField)}
      >
        {convertOptions(options).map(o => (
          <ToggleButton
            key={o.value}
            id={`${fieldName}-${o.value}-button`}
            value={o.value}
            variant={value === o.value ? 'primary' : 'outline-primary'}
          >
            {o.name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Field>
  );
};

const AvailabilityCodeSelector = ({
  multi,
  availabilityCodes,
  selectedAvailabilityCodes,
  description,
  onChange,
  label
}) => (
  <Field label={label} description={description} labelColWidth={4}>
    <GeneralAvailabilityCodeSelector
      multi={multi}
      loading={false}
      availabilityCodes={availabilityCodes}
      onChange={onChange}
      selectedAvailabilityCodes={selectedAvailabilityCodes}
    />
  </Field>
);

const CheckpointSelector = ({ value, checkpoint, name, changeTime, label }) => {
  // Checkpoint is used to prevent the Night shift start hour to be before the
  // day shift start hour
  const hours = [];
  for (let i = checkpoint; i < 24; i++) {
    hours.push(i);
  }
  const minutes = [];
  for (let i = 0; i < 60; i += 15) {
    minutes.push(i);
  }

  return (
    <Form.Group controlId={name}>
      <Form.Label>{label}</Form.Label>
      <Form.InputGroup className="mb-3">
        <Form.Select
          value={value.hour}
          onChange={e => changeTime({ hour: Number(e.target.value), minute: Number(value.minute) })}
        >
          {hours.map(h => (
            <option key={name + h} value={h}>
              {h}
            </option>
          ))}
        </Form.Select>
        <Form.InputGroupText>:</Form.InputGroupText>
        <Form.Select
          value={value.minute}
          onChange={e => changeTime({ hour: Number(value.hour), minute: Number(e.target.value) })}
        >
          {minutes.map(m => (
            <option key={name + m} value={m}>
              {m}
            </option>
          ))}
        </Form.Select>
      </Form.InputGroup>
    </Form.Group>
  );
};

const AvailabilityRequirementsSelector = ({
  groupsTree,
  parentGroupId,
  selectedAvailabilityRequirements,
  onChange
}) => (
  <Field labelColWidth={0}>
    <GeneralAvailabilityRequirementsSelector
      groupsTree={groupsTree}
      parentGroupId={parentGroupId}
      selectedAvailabilityRequirements={selectedAvailabilityRequirements}
      onChange={onChange}
    />
  </Field>
);

const GroupSelector = ({ label, value, groups, onChange, isMulti, isClearable }) => (
  <Field label={label} labelColWidth={4}>
    <GeneralGroupSelector
      value={value}
      groups={groups}
      onChange={onChange}
      isMulti={isMulti}
      isClearable={isClearable}
    />
  </Field>
);

const SkillSelector = ({ selectedSkillOptions, skillOptions, handleChange, label }) => (
  <Field label={label} labelColWidth={4}>
    <ReactSelect
      value={selectedSkillOptions}
      isMulti
      onChange={handleChange}
      options={skillOptions}
      isSearchable={false}
    />
  </Field>
);

const Section = Row;

// Context for setting updateWidgetSettingsField and widgetSettings on the
// settings form once, so it doesn't have to be passed to individual fields
const SettingsFormContext = createContext({
  updateWidgetSettingsField: () => {},
  widgetSettings: {}
});

const SettingsForm = ({
  updateWidgetSettingsField,
  widgetSettings,
  children,
  withAutoScroll,
  withAutoResizeHeight
}) => (
  <SettingsFormContext.Provider value={{ updateWidgetSettingsField, widgetSettings }}>
    {(withAutoScroll || withAutoResizeHeight) && (
      <Section>
        <SettingsForm.Section.Header>{I18n.t('general')}</SettingsForm.Section.Header>
        <SettingsForm.Section.Content>
          {withAutoScroll && (
            <SettingsForm.ToggleField fieldName="autoScrollEnabled" label={I18n.t('dashboards.autoscroll')} />
          )}
          {withAutoResizeHeight && (
            <SettingsForm.ToggleField fieldName="autoResizeHeight" label={I18n.t('dashboards.auto_resize_height')} />
          )}
        </SettingsForm.Section.Content>
      </Section>
    )}
    {children}
  </SettingsFormContext.Provider>
);

Object.assign(Section, {
  Header: props => (
    <Col sm={3} {...props}>
      <h5>{props.children}</h5>
    </Col>
  ),
  Content: props => (
    <Col sm={9} {...props}>
      {props.children}
    </Col>
  )
});

Object.assign(SettingsForm, {
  AvailabilityCodeSelector,
  AvailabilityRequirementsSelector,
  GroupSelector,
  CheckpointSelector,
  SkillSelector,
  Field,
  InputField,
  Section,
  Select,
  ToggleField,
  RadioButtonGroup
});

export default SettingsForm;
